import React, { useEffect, useState } from "react";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import { Title } from "@src/components/atoms/title/title";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";

import { DrawerButtonWrapper } from "@src/components/molecules/drawer-button-wrapper/drawer-button-wrapper";
import { useDispatch, useSelector } from "react-redux";
import {
  updateModalsCloseDrawerModal,
  updateUserListResultsReady,
} from "@src/reducers/your-reducer";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { Button } from "@src/components/atoms/buttons/button/button";

import styles from "./edit-service-user-drawer-modal.module.scss";
import { Role, ZitadelServiceUser } from "@src/types/user-management";
import { RoleSelector } from "@src/components/molecules/roles-selector/roles-selector";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { RootState } from "@src/reducers/store";
import { UserStatusWithSwitch } from "@src/components/atoms/user-status-with-switch/user-status-with-switch";
import { trimUserInputs } from "@src/cloud-connect/user-manager/utils/trim-user-drawer-inputs";
import { validateUserInputs } from "@src/cloud-connect/user-manager/utils/validate-user-drawer-inputs";
import { patchUpdateServiceUserUseCase } from "@src/cloud-connect/user-manager/use-cases/patch-update-service-user-use-case";
import { changeUserStatusUseCase } from "@src/cloud-connect/user-manager/use-cases/change-user-status-use-case";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { getValidUserRolesUseCase } from "@src/cloud-connect/user-manager/use-cases/get-valid-user-roles-use-case";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { useI18N } from "@src/language/language-context";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import Spinner from "@src/components/atoms/spinner/spinner";

const cssClassName = "edit-service-user-drawer-modal";

export const EditServiceUserDrawerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const selectedServiceUserFromUserList: ZitadelServiceUser | null =
    useSelector(
      (state: RootState) => state.yourSlice.selectedServiceUserInEditUserDrawer,
    );

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const texts = {
    editServiceUserTitle: i18n(
      "modules.editServiceUserDrawerModal.editServiceUserTitle",
    ),
    serviceUserUsernameLabel: i18n(
      "modules.editServiceUserDrawerModal.serviceUserUsernameLabel",
    ),
    serviceUserUsernameInputPlaceholder: i18n(
      "modules.editServiceUserDrawerModal.serviceUserUsernameInputPlaceholder",
    ),
    serviceUserNameLabel: i18n(
      "modules.editServiceUserDrawerModal.serviceUserNameLabel",
    ),
    serviceUserNameInputPlaceholder: i18n(
      "modules.editServiceUserDrawerModal.serviceUserNameInputPlaceholder",
    ),
    serviceUserDescriptionLabel: i18n(
      "modules.editServiceUserDrawerModal.serviceUserDescriptionLabel",
    ),
    serviceUserDescriptionInputPlaceholder: i18n(
      "modules.editServiceUserDrawerModal.serviceUserDescriptionInputPlaceholder",
    ),
    expirationDateLabel: i18n(
      "modules.editServiceUserDrawerModal.expirationDateLabel",
    ),
    noExpirationDateText: i18n(
      "modules.editServiceUserDrawerModal.noExpirationDateText",
    ),
    updateServiceUserButtonText: i18n(
      "modules.editServiceUserDrawerModal.updateServiceUserButtonText",
    ),
    cancelButtonText: i18n(
      "modules.editServiceUserDrawerModal.cancelButtonText",
    ),
    activeStatusText: i18n(
      "modules.editServiceUserDrawerModal.activeStatusText",
    ),
    inactiveStatusText: i18n(
      "modules.editServiceUserDrawerModal.inactiveStatusText",
    ),
    initialStatusText: i18n(
      "modules.editServiceUserDrawerModal.initialStatusText",
    ),
    rolesWarningText: i18n(
      "modules.editServiceUserDrawerModal.rolesWarningText",
    ),
    inputsCheckWarningText: i18n(
      "modules.editServiceUserDrawerModal.inputsCheckWarningText",
    ),
    userUpdatedSuccessText: i18n(
      "modules.editServiceUserDrawerModal.userUpdatedSuccessText",
    ),
    detailsAndStatusErrorText: i18n(
      "modules.editServiceUserDrawerModal.detailsAndStatusErrorText",
    ),
    detailsErrorText: i18n(
      "modules.editServiceUserDrawerModal.detailsErrorText",
    ),
    statusErrorText: i18n("modules.editServiceUserDrawerModal.statusErrorText"),
  };

  const [updatingServiceUser, setUpdatingServiceUser] = useState(false);
  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);

  const [serviceUser, setServiceUser] =
    useState<Partial<ZitadelServiceUser> | null>(
      selectedServiceUserFromUserList,
    );

  const isStatusModified =
    serviceUser?.status !== selectedServiceUserFromUserList?.status;

  const loadValidUserRoles = async () => {
    const roles = await getValidUserRolesUseCase();
    setRoleOptions(roles.data || []);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (authContext?.permissions?.includes("manage-service-api-users")) {
        await loadValidUserRoles();
      }
      setLoading(false);
    };

    loadData();
  }, [authContext?.permissions]);

  const handleEditUser = async () => {
    setUpdatingServiceUser(true);

    if (serviceUser.roles.length === 0) {
      warningSnackbar(texts.rolesWarningText, 5000);
      setUpdatingServiceUser(false);
      return;
    }

    const trimmedUser = trimUserInputs(serviceUser);
    const { isValid, errors } = validateUserInputs(trimmedUser);

    if (!isValid) {
      warningSnackbar(
        `${texts.inputsCheckWarningText}: ${errors.join(", ")}`,
        5000,
      );
      setUpdatingServiceUser(false);
      return;
    }

    const [responseServiceUser, responseStatus] = await Promise.all([
      patchUpdateServiceUserUseCase(trimmedUser as ZitadelServiceUser),
      isStatusModified && serviceUser.status !== "INITIAL"
        ? changeUserStatusUseCase(serviceUser.id, serviceUser.status)
        : Promise.resolve({ success: true }),
    ]);

    const serviceUserOk = responseServiceUser.success;
    const statusOk = responseStatus.success;
    const bothOk = serviceUserOk && statusOk && responseServiceUser.data;

    const dateToTriggerReload = new Date().toISOString();

    if (bothOk) {
      successSnackbar(texts.userUpdatedSuccessText, 5000);
      dispatch(updateUserListResultsReady(dateToTriggerReload));
    } else {
      const messages: string[] = [];
      if (!serviceUserOk) {
        messages.push(responseServiceUser.error || texts.detailsErrorText);
      }

      if (!statusOk) {
        messages.push(
          ("error" in responseStatus &&
            typeof responseStatus.error === "string" &&
            responseStatus.error) ||
            texts.statusErrorText,
        );
      }
      errorSnackbar(messages.join(". ") + ".", 5000);
      dispatch(updateUserListResultsReady(dateToTriggerReload));
    }

    setUpdatingServiceUser(false);

    closeDrawer();
  };
  const returnStatusText = (status: string | null) => {
    switch (status) {
      case "ACTIVE":
        return texts.activeStatusText;
      case "INACTIVE":
        return texts.inactiveStatusText;
      case "INITIAL":
        return texts.initialStatusText;
      default:
        return texts.initialStatusText;
    }
  };

  const closeDrawer = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "editServiceUserDrawerModal",
      }),
    );
  };

  return (
    <DrawerModal id={"editServiceUserDrawerModal"}>
      <div className={styles[cssClassName]}>
        <FlexColumnContainer verticalGap={32}>
          <Title tag={"h2"} text={texts.editServiceUserTitle} />
          {loading ? (
            <Spinner />
          ) : (
            <div className={styles[`${cssClassName}__content-wrapper`]}>
              <Input2025
                labelText={texts.serviceUserUsernameLabel}
                inputPlaceHolder={texts.serviceUserUsernameInputPlaceholder}
                inputValue={serviceUser.username || ""}
                onChangeFunc={(e) =>
                  setServiceUser({ ...serviceUser, username: e.target.value })
                }
              />
              <Input2025
                labelText={texts.serviceUserNameLabel}
                inputPlaceHolder={texts.serviceUserNameInputPlaceholder}
                inputValue={serviceUser.name || ""}
                onChangeFunc={(e) =>
                  setServiceUser({ ...serviceUser, name: e.target.value })
                }
              />
              <Input2025
                labelText={texts.serviceUserDescriptionLabel}
                inputPlaceHolder={texts.serviceUserDescriptionInputPlaceholder}
                inputValue={serviceUser.description || ""}
                onChangeFunc={(e) =>
                  setServiceUser({
                    ...serviceUser,
                    description: e.target.value,
                  })
                }
              />
              <RenderIfUserHasPermission permission="manage-service-api-users">
                <UserStatusWithSwitch
                  text={returnStatusText(serviceUser.status)}
                  setStatus={() =>
                    setServiceUser({
                      ...serviceUser,
                      status:
                        serviceUser.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                    })
                  }
                  status={serviceUser.status}
                />
                <RoleSelector
                  user={serviceUser}
                  setUser={setServiceUser}
                  roleOptions={roleOptions}
                />
              </RenderIfUserHasPermission>
              <div
                className={styles[`${cssClassName}__expiration-date-wrapper`]}
              >
                <p className={styles[`${cssClassName}__expiration-date-label`]}>
                  {texts.expirationDateLabel}
                </p>
                <p className={styles[`${cssClassName}__expiration-date-text`]}>
                  {serviceUser.expiration_date ? (
                    <TimezoneDate date={serviceUser.expiration_date} />
                  ) : (
                    texts.noExpirationDateText
                  )}
                </p>
              </div>
              <DrawerButtonWrapper>
                <ButtonWithLoader
                  fun={handleEditUser}
                  text={texts.updateServiceUserButtonText}
                  variant={"full-width"}
                  loading={updatingServiceUser}
                />
                <Button
                  fun={closeDrawer}
                  text={texts.cancelButtonText}
                  variant={["secondary", "full-width"]}
                />
              </DrawerButtonWrapper>
            </div>
          )}
        </FlexColumnContainer>
      </div>
    </DrawerModal>
  );
};
